import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Zap, Shield, Scale, Rocket, Loader2, Check, ArrowRight, TrendingUp, Sparkles } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RISK_TIERS = [
  { value: "conservative", label: "Conservative", icon: Shield, description: "Lower risk, steadier growth", color: "text-blue-400", ring: "ring-blue-500" },
  { value: "moderate", label: "Moderate", icon: Scale, description: "Balanced risk and reward", color: "text-green-400", ring: "ring-green-500" },
  { value: "aggressive", label: "Aggressive", icon: Rocket, description: "Higher growth, higher swings", color: "text-red-400", ring: "ring-red-500" },
];

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: 0 }).format(value || 0);

export default function QuickStartPortfolio() {
  const navigate = useNavigate();
  const [amount, setAmount] = useState(1000);
  const [riskTier, setRiskTier] = useState(null);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const createPortfolio = async () => {
    if (!name.trim()) {
      toast.error("Give your portfolio a name");
      return;
    }
    if (!riskTier) {
      toast.error("Choose a risk appetite");
      return;
    }
    if (!amount || amount <= 0) {
      toast.error("Enter an amount to invest");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/quick-start/create`, {
        name: name.trim(),
        amount: parseFloat(amount),
        risk_tier: riskTier,
      });
      setResult(data);
      toast.success("Portfolio created!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create portfolio");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setResult(null);
    setName("");
    setAmount(1000);
    setRiskTier(null);
  };

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-slide-up" data-testid="quick-start-page">
      <div>
        <h1
          className="text-2xl sm:text-3xl font-bold tracking-tight uppercase flex items-center gap-2"
          style={{ fontFamily: "Barlow Condensed, sans-serif" }}
        >
          <Zap className="w-7 h-7 text-primary" />
          Quick Start
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Two questions. One AI-built HL portfolio, grounded in real funds you can actually buy.
        </p>
      </div>

      {!result ? (
        <Card data-testid="quick-start-form">
          <CardContent className="pt-6 space-y-8">
            <div className="space-y-2">
              <Label>Portfolio name</Label>
              <Input
                placeholder="e.g., My New ISA"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="quick-start-name-input"
              />
            </div>

            <div className="space-y-3">
              <Label>How much would you like to invest?</Label>
              <div className="text-3xl font-bold text-primary" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
                {formatCurrency(amount)}
              </div>
              <Input
                type="number"
                min="1"
                value={amount}
                onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
                data-testid="quick-start-amount-input"
              />
              <div className="grid grid-cols-4 gap-2">
                {[500, 1000, 2500, 5000].map((a) => (
                  <Button
                    key={a}
                    variant={amount === a ? "default" : "outline"}
                    size="sm"
                    onClick={() => setAmount(a)}
                    data-testid={`quick-start-amount-${a}`}
                  >
                    {formatCurrency(a)}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <Label>What's your risk appetite?</Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {RISK_TIERS.map((tier) => {
                  const Icon = tier.icon;
                  return (
                    <Card
                      key={tier.value}
                      className={`cursor-pointer transition-all ${
                        riskTier === tier.value ? `ring-2 ${tier.ring} border-primary` : "hover:border-primary/50"
                      }`}
                      onClick={() => setRiskTier(tier.value)}
                      data-testid={`quick-start-risk-${tier.value}`}
                    >
                      <CardContent className="pt-6 text-center">
                        <Icon className={`w-8 h-8 mx-auto mb-2 ${tier.color}`} />
                        <p className="font-bold">{tier.label}</p>
                        <p className="text-xs text-muted-foreground mt-1">{tier.description}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>

            <Button
              onClick={createPortfolio}
              disabled={loading}
              className="w-full bg-gradient-to-r from-primary to-green-500"
              size="lg"
              data-testid="quick-start-create-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Building your portfolio...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" /> Create My Portfolio
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6" data-testid="quick-start-result">
          <Card className="border-green-500/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Check className="w-5 h-5 text-green-500" />
                {result.portfolio.name} is ready
              </CardTitle>
              <CardDescription>{result.ai_summary}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {result.portfolio.holdings.map((h, idx) => (
                <div key={idx} className="p-3 bg-muted/30 rounded-md" data-testid={`quick-start-holding-${idx}`}>
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-sm">{h.name}</p>
                    <div className="text-right">
                      <p className="font-bold text-sm">{formatCurrency(h.value)}</p>
                      <p className="text-xs text-muted-foreground">{h.allocation_pct}%</p>
                    </div>
                  </div>
                  {h.reason && <p className="text-xs text-muted-foreground mt-1">{h.reason}</p>}
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="border-amber-500/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-amber-500" />
                5-Year Monte Carlo Forecast
              </CardTitle>
              <CardDescription>
                1,000 simulated market paths from today's {formatCurrency(result.monte_carlo.starting_amount)}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-red-500/10 rounded-md text-center">
                  <p className="text-xs text-muted-foreground mb-1">Worst Case (10%)</p>
                  <p className="text-lg font-bold text-red-400">{formatCurrency(result.monte_carlo.worst_case_10pct)}</p>
                </div>
                <div className="p-3 bg-primary/10 rounded-md text-center">
                  <p className="text-xs text-muted-foreground mb-1">Median</p>
                  <p className="text-lg font-bold text-primary">{formatCurrency(result.monte_carlo.median)}</p>
                </div>
                <div className="p-3 bg-green-500/10 rounded-md text-center">
                  <p className="text-xs text-muted-foreground mb-1">Best Case (90%)</p>
                  <p className="text-lg font-bold text-green-400">{formatCurrency(result.monte_carlo.best_case_90pct)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={reset} data-testid="quick-start-another-btn">
              Start Another
            </Button>
            <Button
              className="flex-1 bg-gradient-to-r from-primary to-green-500"
              onClick={() => navigate("/my-portfolios")}
              data-testid="quick-start-view-portfolios-btn"
            >
              View My Portfolios <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
